import { useState, type ReactElement } from 'react';
import { useT } from '../../../../app/di';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Button } from '../../../../ui-kit/Button/Button';
import styles from './IssuedApiKey.module.scss';

enum CopyState {
  Idle = 'idle',
  Copied = 'copied',
  Failed = 'failed',
}

export interface IssuedApiKeyProps {
  secret: string;
  /** Текст над ключом; по умолчанию — «скопируйте сейчас, больше не покажем» (IAM-RULE-4). */
  notice?: string;
}

/** IAM-RULE-4: ключ целиком — с копированием и предупреждением. Он же — тестовый ключ демо (DEMO-RULE-2). */
export function IssuedApiKey({ secret, notice }: IssuedApiKeyProps): ReactElement {
  const { t } = useT();
  const [copy, setCopy] = useState(CopyState.Idle);
  const copySecret = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopy(CopyState.Copied);
    } catch {
      setCopy(CopyState.Failed);
    }
  };
  return (
    <div className="u-stack">
      <Alert tone="warning">{notice ?? t('identity.apiKeys.secretWarning')}</Alert>
      <div className="u-stack u-gap-1">
        <span className="u-small">{t('identity.apiKeys.secretLabel')}</span>
        <code className={styles.secret} aria-label={t('identity.apiKeys.secretLabel')}>
          {secret}
        </code>
      </div>
      <div className="u-row">
        <Button icon={copy === CopyState.Copied ? 'check' : 'copy'} onClick={() => void copySecret()}>
          {copy === CopyState.Copied ? t('identity.apiKeys.copied') : t('identity.apiKeys.copy')}
        </Button>
      </div>
      {copy === CopyState.Failed ? <Alert tone="danger">{t('identity.apiKeys.copyFailed')}</Alert> : null}
    </div>
  );
}
